import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { logPageView } from '../utils/firebase';
import { connect } from '../data/connect';
import { Project, RootState } from '../types';

interface SeoOwnProps {
  quote?: string;
  image?: string;
  hashtag?: string;
}

interface SeoStateProps {
  projects: Project[];
}

type SeoProps = SeoOwnProps & SeoStateProps;

function truncateMetaDescription(str: string, length?: number, ending?: string): string {
  const maxLength = length ?? 100;
  const suffix = ending ?? '';

  if (str.length > maxLength) {
    return str.substring(0, maxLength - suffix.length) + suffix;
  }
  return str;
}

const Seo: React.FC<SeoProps> = ({ projects, quote = '', image, hashtag }) => {
  const location = useLocation();
  const currentUrl = 'https://amiprojects.io' + location.pathname;
  const displayQuote = quote;
  const displayImage = image ?? 'https://amiprojects.io/assets/share.jpg';
  const displayHashtag = hashtag ?? '#AMIprojects';

  const [title, setTitle] = useState('AMI Projects');
  const [description, setDescription] = useState(
    "AMI Projects is Anna Maria Island's official website for the capitial improvement projects happening on the island. View project information and stay up to date with real time alerts."
  );

  useEffect(() => {
    const slug = location.pathname.split('/').pop();

    if (location.pathname.startsWith('/projects/') && slug) {
      const project = projects.find((p) => p.attributes.slug === slug);
      if (project) {
        const selectedProject = project.attributes;
        const desc =
          "Learn about Anna Maria Island's " +
          selectedProject.title +
          ' capital improvement project. ' +
          selectedProject.description;
        setDescription(truncateMetaDescription(desc, 250));
        setTitle(selectedProject.title);
        return;
      }
    }

    if (location.pathname === '/') {
      setTitle('Home');
      setDescription(
        "AMI Projects is Anna Maria Island's official website for the capitial improvement projects happening on the island. View project information and stay up to date with real time alerts."
      );
    }
    if (location.pathname === '/contact') {
      setTitle('Contact');
      setDescription(
        'Have questions or concerns about an Anna Maria Island improvement project? Reach out to a project representative by phone, email, or contact form any time time of the day.'
      );
    }
    if (location.pathname === '/resources') {
      setTitle('Resources');
      setDescription(
        'View documents and additional resources related to the improvement projects happening throughout Anna Maria Island.'
      );
    }
  }, [location.pathname, projects]);

  const history = useHistory();
  useEffect(() => {
    // Lazily load Firebase analytics and log page view
    if (history.length > 1) {
      logPageView(title);
    }
  }, [title, history.length]);

  return (
    <Helmet titleTemplate="%s | AMI Projects" title={title}>
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta property="type" content="website" />
      <meta property="url" content={currentUrl} />
      <meta name="robots" content="noodp" />
      <meta property="title" content={title} />
      <meta property="quote" content={displayQuote} />
      <meta name="description" content={description} />
      <meta property="image" content={displayImage} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:quote" content={displayQuote} />
      <meta property="og:hashtag" content={displayHashtag} />
      <meta property="og:image" content={displayImage} />
      <meta content="image/*" property="og:image:type" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="AMI Projects" />
      <meta property="og:description" content={description} />
    </Helmet>
  );
};

export default connect<SeoStateProps, Record<string, never>, SeoOwnProps>({
  mapStateToProps: (state: RootState) => ({
    projects: state.data.projects,
  }),
  component: Seo,
});
